import { useCallback } from 'react'

import { BasePath } from 'routes/paths'

import { ChildOrg, OrgByParent } from 'api/types/orgsApiTypes'
import { registerCommands, navCommand, runCommand } from 'components/cmdk'
import { useOrg } from 'contexts/OrgContext'
import { useOrgsByParent } from 'hooks/data/orgsHook'
import { useSettingTabs } from 'hooks/settings/useSettingTabs'
import { useOrgDisplayWithSlug } from 'hooks/utils/useOrgDisplayWithSlug'

const getSettingsPath = (route: string) =>
  route.startsWith(BasePath.Settings) ? route : `${BasePath.Settings}/${route}`

export const useCommandRegistry = () => {
  const { setOrg } = useOrg()
  const { data: orgs } = useOrgsByParent()
  const tabs = useSettingTabs()
  const { formatOrgDisplay } = useOrgDisplayWithSlug()

  return useCallback(() => {
    const getShowName = (org: OrgByParent | ChildOrg) => formatOrgDisplay(org)
    const getEntityLabel = (parent: OrgByParent, entity: ChildOrg) =>
      `${getShowName(entity)} (${getShowName(parent)})`
    // Register settings commands
    registerCommands(() => ({
      name: 'Settings',
      commands: Object.values(tabs).map((t) =>
        navCommand({
          label: t.title,
          iconName: 'external-link',
          path: getSettingsPath(t.route),
        })
      ),
    }))

    // Register organization commands
    registerCommands(() => ({
      name: 'Organizations',
      commands:
        orgs?.flatMap((org: OrgByParent) => [
          runCommand({
            label: getShowName(org),
            iconName: 'external-link',
            run: ({ controls: { close } }) => close(setOrg(org.org_name)),
          }),
          ...org.children.map((entity) =>
            runCommand({
              label: getEntityLabel(org, entity),
              iconName: 'external-link',
              run: ({ controls: { close } }) => close(setOrg(entity.org_name)),
            })
          ),
        ]) ?? [],
    }))
  }, [tabs, orgs, setOrg, formatOrgDisplay])
}
